import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { streamAgentResponse } from '@/api/agent';
import {
  buildExportMarkdown as buildExportMarkdownShared,
  exportConversation as exportConversationShared,
} from '@/utils/agentExport';
import useResourcesStore from '@/stores/resourcesStore';

const useAgentStore = create()(
  immer(
    persist(
      () => ({
        messages: [],        // [{role: 'user'|'assistant', content: string}]
        currentProjectId: null,
        isOpen: false,
        isStreaming: false,
        abortController: null,  // transient, not persisted
        _nextTextIsNewBubble: false,  // start a fresh bubble after each tool call
        pendingActions: [],  // write-tool events for the current turn's progress display
        // Live-streaming text for the FINAL post-tool message. Accumulated
        // alongside the intermediate bubble (which is hidden from the UI
        // until onDone). The panel renders this transient field as a live
        // bubble when all pendingActions are done, giving the user a
        // "typing" indicator during the 5-15 s post-tool generation gap.
        // Cleared at tool boundaries and on onDone. Never persisted.
        streamingText: '',
      }),
      {
        name: 'sesame-agent',
        // Bumped from 2 → 3 when the sessionContext field was removed; the
        // bump invalidates any older persisted state that still carried it.
        version: 3,
        // Persist conversation + project. isOpen, isStreaming,
        // _nextTextIsNewBubble, pendingActions reset on load.
        partialize: state => ({
          messages: state.messages,
          currentProjectId: state.currentProjectId,
        }),
      },
    ),
  ),
);

const { setState: set, getState: get } = useAgentStore;

// Resolve a project's display label and absolute dashboard URL for export.
// The project record lives in resourcesStore (loaded by the project route);
// fall back to the bare ID when name isn't available.
function projectInfoForExport(projectId) {
  if (!projectId) return {};
  const project = useResourcesStore.getState().project;
  const projectLabel = project && project.id === projectId ? project.name : null;
  const projectUrl = `${window.location.origin}/projects/${projectId}/dashboard`;
  return { projectId, projectLabel, projectUrl };
}

export const agentActions = {
  open: () => set(s => { s.isOpen = true; }),
  close: () => set(s => { s.isOpen = false; }),
  toggle: () => set(s => { s.isOpen = !s.isOpen; }),

  clearConversation: () => set(s => {
    s.messages = [];
    s.isStreaming = false;
    s.abortController = null;
    s.pendingActions = [];
    s.streamingText = '';
  }),

  stopStreaming: () => {
    const { abortController } = get();
    abortController?.abort();
    set(s => {
      s.isStreaming = false;
      s.abortController = null;
      // Commit any already-completed actions into history so they're not lost.
      const completedActions = s.pendingActions.filter(a => a.status === 'done');
      if (completedActions.length > 0) {
        s.messages.push({ role: 'actions', actions: completedActions });
      }
      s.pendingActions = [];
      s.streamingText = '';
      s.messages.push({ role: 'assistant', content: 'Stopped.' });
    });
  },

  buildExportMarkdown: () => {
    const { messages, currentProjectId } = get();
    return buildExportMarkdownShared({
      messages,
      ...projectInfoForExport(currentProjectId),
    });
  },

  exportConversation: () => {
    const { messages, currentProjectId } = get();
    exportConversationShared({
      messages,
      ...projectInfoForExport(currentProjectId),
    });
  },

  /**
   * Called by AgentPanel on mount and whenever projectId changes.
   * Clears the stored conversation when the user switches to a different project
   * so they don't see a stale conversation from another project.
   */
  syncProject: projectId => {
    const { currentProjectId } = get();
    if (currentProjectId && currentProjectId !== projectId) {
      set(s => {
        s.messages = [];
        s.isStreaming = false;
        s.currentProjectId = projectId;
      });
    } else {
      set(s => { s.currentProjectId = projectId; });
    }
  },

  /**
   * Send a message to the agent and stream the response.
   *
   * @param {object}   params
   * @param {string}   params.projectId
   * @param {string}   params.message
   * @param {object}   [params.navigationContext]
   * @param {Function} [params.onAction]  - extra side-effect called for every action event
   */
  sendMessage: async ({ projectId, message, navigationContext, onAction }) => {
    if (!message.trim() || get().isStreaming) return;

    // Capture prior conversation before adding the new user turn.
    // Exclude 'actions' entries (no content field) and hidden intermediate
    // assistant messages — neither belongs in the LLM's conversation history.
    const previousMessages = get().messages
      .filter(m => (m.role === 'user' || m.role === 'assistant') && !m.intermediate && m.role !== 'thinking')
      .map(m => ({ role: m.role, content: m.content }));

    const controller = new AbortController();
    set(s => {
      s.messages.push({ role: 'user', content: message });
      s.isStreaming = true;
      s.abortController = controller;
      s.pendingActions = [];
      s.streamingText = '';
    });

    try {
      await streamAgentResponse({
        projectId,
        message,
        conversation: previousMessages,
        navigationContext,
        signal: controller.signal,
        onPlan: actions => {
          // Announce all tools in the coming batch as pending before any executes.
          set(s => {
            s._nextTextIsNewBubble = true;
            // Discard any text that streamed since the previous tool boundary
            // — it was intermediate narration ("let me check structure...")
            // and the next round will produce its own text bubble.
            s.streamingText = '';
            for (const actionType of actions) {
              s.pendingActions.push({ actionType, data: null, status: 'pending' });
            }
          });
        },

        onActionStarted: actionType => {
          set(s => {
            s._nextTextIsNewBubble = true;
            s.streamingText = '';
            // Transition the first matching pending entry to running, or push
            // a new running entry if there was no prior plan event for this tool.
            const pendingIdx = s.pendingActions.findIndex(
              a => a.actionType === actionType && a.status === 'pending',
            );
            if (pendingIdx !== -1) {
              s.pendingActions[pendingIdx].status = 'running';
            } else {
              // Agent is retrying a previously-failed tool. Remove the stale
              // error entry so the UI doesn't permanently show a ✗ for a step
              // that ultimately succeeds (same rule as analystStore).
              const errorIdx = s.pendingActions.findIndex(
                a => a.actionType === actionType && a.status === 'done' && a.data?.error,
              );
              if (errorIdx !== -1) s.pendingActions.splice(errorIdx, 1);
              s.pendingActions.push({ actionType, data: null, status: 'running' });
            }
          });
        },

        onThinking: text => {
          set(s => {
            const last = s.messages[s.messages.length - 1];
            if (last?.role === 'thinking') {
              last.content += text;
            } else {
              s.messages.push({ role: 'thinking', content: text });
            }
          });
        },

        onText: text => {
          set(s => {
            const startNew = s._nextTextIsNewBubble;
            s._nextTextIsNewBubble = false;
            const last = s.messages[s.messages.length - 1];
            if (!startNew && last?.role === 'assistant') {
              last.content += text;
            } else {
              // Messages that begin after tool calls are intermediate narration
              // ("Great! Now let me balance..."). They're hidden from the UI
              // while the final summary message streams in their place.
              // On done, the last assistant message is un-marked.
              s.messages.push({
                role: 'assistant',
                content: text,
                intermediate: s.pendingActions.length > 0,
              });
            }
            // Also accumulate into the live streamingText buffer when the
            // current bubble would otherwise be hidden as intermediate. The
            // panel renders streamingText as a live "typing" bubble once all
            // pendingActions are done — covers the post-tool generation gap
            // that previously looked blank. Pre-tool text (pendingActions
            // empty) renders through the regular message bubble, so don't
            // duplicate it here.
            if (s.pendingActions.length > 0) {
              s.streamingText += text;
            }
          });
        },

        onAction: (actionType, data) => {
          console.log('[Agent action]', actionType, data);
          set(s => {
            s._nextTextIsNewBubble = true;
            // Promote the first matching 'running' placeholder to 'done',
            // or push a new completed entry if there was no prior started event.
            const runningIdx = s.pendingActions.findIndex(
              a => a.actionType === actionType && a.status === 'running',
            );
            if (runningIdx !== -1) {
              s.pendingActions[runningIdx] = { actionType, data, status: 'done' };
            } else {
              s.pendingActions.push({ actionType, data, status: 'done' });
            }
          });
          onAction?.(actionType, data);
        },

        onDebugStats: data => {
          console.log('[Agent debug]', data);
          set(s => {
            for (let i = s.messages.length - 1; i >= 0; i--) {
              if (s.messages[i].role === 'assistant') {
                s.messages[i].debugStats = data;
                break;
              }
            }
          });
        },

        onDone: () => set(s => {
          s.isStreaming = false;
          s.abortController = null;
          // Un-mark the last assistant message (the final summary) and
          // commit the progress actions into the message thread right before
          // it, so they stay in place in the conversation history.
          for (let i = s.messages.length - 1; i >= 0; i--) {
            if (s.messages[i].role === 'assistant') {
              s.messages[i].intermediate = false;
              if (s.pendingActions.length > 0) {
                s.messages.splice(i, 0, {
                  role: 'actions',
                  actions: [...s.pendingActions],
                });
              }
              break;
            }
          }
          s.pendingActions = [];
          // The committed assistant bubble now renders the final text;
          // clear the transient streaming buffer so it isn't duplicated.
          s.streamingText = '';
        }),

        onError: errorMessage => {
          set(s => {
            s.isStreaming = false;
            s.abortController = null;
            s.streamingText = '';
            s.messages.push({
              role: 'assistant',
              content: `Something went wrong: ${errorMessage}`,
            });
          });
        },
      });
    } catch (err) {
      set(s => {
        s.isStreaming = false;
        s.abortController = null;
        s.streamingText = '';
        s.messages.push({
          role: 'assistant',
          content: 'Something went wrong. Please try again.',
        });
      });
    }
  },
};

export default useAgentStore;
